import { useSuspenseQuery } from '@tanstack/react-query';
import { BookOpenText, Download, History, Pencil, Share2, Trash2 } from 'lucide-react';
import { Component, type ReactNode, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { isChilldocName, type MyFileItem } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { humanSize } from '../../../lib/bytes';
import { errorMessage } from '../../../lib/error-message';
import { ApiRequestError } from '../../../lib/http';
import { myFilesKeys } from '../api';
import { FileTypeIcon } from '../file-icon';
import { PreviewError } from './preview-error';
import { PreviewAction, PreviewFrame } from './preview-frame';
import { CopyLinkAction, linkHrefOf } from './preview-link';
import { MY_FILES_SOURCE, usePreviewSource } from './preview-source';
import { PreviewControls } from './viewer-controls';
import { type PreviewableViewer, viewerFor } from './viewer-registry';

/**
 * Khung xem trước toàn màn hình — APM-MYF-d, docs/plan-my-files.md §4.3.
 *
 * Khung và thanh nút là `PreviewFrame`, **chung** với tài liệu dự án
 * (`DocumentPreviewDialog`) — hai nơi xem một tệp trông và bấm như nhau
 * (2026-10-02). Nút chỉ-biểu-tượng, cùng thứ tự: Sửa · Chia sẻ · Phiên bản ·
 * Chép link · Tải về · Xoá · Đóng. Link (APM-PVLINK, `linkHrefOf`) mở đúng khung
 * này cho chủ tệp và người được chia sẻ; tệp thuộc dự án thì dẫn về chỗ gốc
 * trong dự án — nơi thành viên có quyền xem nó. Mỗi nút cùng điều kiện với menu của dòng và chỉ hiện
 * khi nơi mở khung truyền đường vào (`onEdit`, `onShare`, `onVersions`,
 * `onDelete`). Không có nút/phím đi qua tệp trước/sau: mũi tên trái/phải thuộc
 * về trình xem (lật slide, cuộn bảng).
 *
 * Thân: trình xem chọn theo `viewerFor` (đuôi trước, MIME sau). Các lối ra khác
 * đều về **cùng một màn hình** — biểu tượng lớn + lý do + nút tải về: định dạng
 * chưa hỗ trợ hoặc chỉ tải về (`.ppt`), vượt trần dung lượng, trình duyệt quá
 * cũ cho thư viện (pdf.js), và mọi lỗi (đọc file, nạp thư viện, thư viện ném,
 * trình duyệt không phát được). Không bao giờ màn hình trắng.
 *
 * Luật #3: nội dung đi thẳng S3 → trình duyệt qua presigned GET; không byte nào
 * qua API.
 *
 * ⚠️ Không nhận asOf: xem trạng thái **hiện tại** của một file, không vẽ đại
 * lượng nào theo thời gian (plan §2.8) — cùng lý do với `MyFilesPage`.
 */
export function FilePreviewDialog({
  item,
  linkable = true,
  onClose,
  onDownload,
  onEdit,
  onRead,
  onShare,
  onVersions,
  onDelete,
}: {
  readonly item: MyFileItem;
  /** `false` ⇒ không có nút "Chép link" (bản cũ trong "Các phiên bản": link luôn mở bản mới nhất). */
  readonly linkable?: boolean;
  readonly onClose: () => void;
  readonly onDownload: (item: MyFileItem) => void;
  /** Mở hộp soạn cho tệp `canEdit`. Bỏ trống = không hiện nút "Sửa". */
  readonly onEdit?: (item: MyFileItem) => void;
  /**
   * Mở thẳng hộp soạn ở **chế độ đọc** (APM-CD2-a) — chỉ `.chilldoc` sửa được, vì chế độ đọc là
   * của trình soạn chilldoc. Bỏ trống = không hiện nút "Chế độ đọc".
   */
  readonly onRead?: (item: MyFileItem) => void;
  /** Mở hộp chia sẻ cho tệp của tôi. Bỏ trống = không hiện nút "Chia sẻ". */
  readonly onShare?: (item: MyFileItem) => void;
  /** Mở hộp "Các phiên bản" (tệp của tôi có từ hai bản). Bỏ trống = không hiện. */
  readonly onVersions?: (item: MyFileItem) => void;
  /** Hỏi rồi xoá. Bỏ trống = không hiện nút "Xoá". */
  readonly onDelete?: (item: MyFileItem) => void;
}): ReactNode {
  const { t } = useTranslation();
  // Ô cho thanh công cụ của trình xem — cùng dòng với tên và nút, không thêm dòng.
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);
  const isFile = item.kind === 'file';
  const linkHref = linkable ? linkHrefOf(item) : null;

  return (
    <PreviewFrame
      label={item.name}
      icon={<FileTypeIcon name={item.name} mimeType={item.mimeType} kind={item.kind} />}
      title={item.name}
      meta={item.sizeBytes === null ? null : humanSize(item.sizeBytes)}
      toolbarSlotRef={setToolbarSlot}
      onClose={onClose}
      actions={
        <>
          {item.canEdit && onEdit ? (
            <PreviewAction
              label={t('myFiles.edit')}
              icon={<Pencil aria-hidden />}
              onClick={() => onEdit(item)}
            />
          ) : null}
          {item.canEdit && isFile && isChilldocName(item.name) && onRead ? (
            <PreviewAction
              label={t('chilldocReading.enter')}
              icon={<BookOpenText aria-hidden />}
              onClick={() => onRead(item)}
            />
          ) : null}
          {isFile && item.source === 'mine' && onShare ? (
            <PreviewAction
              label={t('myFiles.fileShare.menu')}
              icon={<Share2 aria-hidden />}
              onClick={() => onShare(item)}
            />
          ) : null}
          {/* Cùng điều kiện với mục "Các phiên bản" của dòng: bản của tài liệu dự án
              xem ở "Phiên bản & duyệt", hộp này chỉ đọc tệp cá nhân. */}
          {item.versions > 1 && item.source === 'mine' && onVersions ? (
            <PreviewAction
              label={`${t('myFiles.versions.menu')} (${item.versions})`}
              icon={<History aria-hidden />}
              onClick={() => onVersions(item)}
            />
          ) : null}
          {linkHref ? <CopyLinkAction href={linkHref} /> : null}
          <PreviewAction
            label={t('myFiles.download')}
            icon={<Download aria-hidden />}
            onClick={() => onDownload(item)}
          />
          {isFile && onDelete ? (
            <PreviewAction
              label={t('myFiles.delete')}
              icon={<Trash2 aria-hidden />}
              destructive
              disabled={!item.canDelete}
              // Khoá kèm lý do như mục Xoá của dòng — người ta cần biết vì sao.
              reason={
                item.source === 'document'
                  ? t('myFiles.cannotDeleteDocument')
                  : t('myFiles.deleteAtOrigin')
              }
              onClick={() => onDelete(item)}
            />
          ) : null}
        </>
      }
    >
      <PreviewBody key={item.id} item={item} onDownload={onDownload} toolbarSlot={toolbarSlot} />
    </PreviewFrame>
  );
}

/**
 * Những gì thân khung cần biết về một tệp — một `MyFileItem` là đủ, và một
 * phiên bản tài liệu dự án dựng được nó (APM-PDOC-d, cùng `PreviewSourceProvider`).
 */
export type PreviewItem = Pick<MyFileItem, 'id' | 'name' | 'mimeType' | 'sizeBytes' | 'kind'>;

/** Thân khung: chọn trình xem, kiểm trần, bọc Suspense + ranh giới lỗi. */
export function PreviewBody<T extends PreviewItem>({
  item,
  onDownload,
  toolbarSlot = null,
}: {
  readonly item: T;
  readonly onDownload: (item: T) => void;
  /** Ô trong header của hộp để đặt thanh công cụ — xem `PreviewControls`. */
  readonly toolbarSlot?: HTMLElement | null;
}): ReactNode {
  const { t } = useTranslation();
  const entry = viewerFor(item.name, item.mimeType);

  if (!entry || entry.downloadOnly) {
    return (
      <PreviewFallback
        item={item}
        title={t('myFiles.preview.unsupported')}
        reason={
          entry?.downloadOnly
            ? t(`myFiles.preview.downloadOnly.${entry.downloadOnly}`)
            : t('myFiles.preview.unsupportedHelp')
        }
        onDownload={onDownload}
      />
    );
  }
  const refusal =
    entry.maxBytes !== null && item.sizeBytes !== null && item.sizeBytes > entry.maxBytes
      ? new PreviewError('tooLarge', { limit: entry.maxBytes })
      : entry.browserSupported && !entry.browserSupported()
        ? new PreviewError('browser')
        : null;
  if (refusal) {
    return (
      <PreviewFallback
        item={item}
        title={t('myFiles.preview.errorTitle')}
        reason={reasonOf(refusal, t)}
        onDownload={onDownload}
      />
    );
  }

  // Thanh công cụ (zoom; trang, outline, tìm ở APM-PVW-b/c/d) bọc **ngoài** ranh
  // giới lỗi: trình xem hỏng thì màn hình tải về vẫn hiện trong vùng cuộn.
  return (
    <PreviewControls kind={entry.zoom} domSearch={entry.search === 'dom'} toolbarSlot={toolbarSlot}>
      <PreviewBoundary
        fallback={(error) => (
          <PreviewFallback
            item={item}
            title={t('myFiles.preview.errorTitle')}
            reason={reasonOf(error, t)}
            onDownload={onDownload}
          />
        )}
      >
        <Suspense
          fallback={
            <p role="status" className="p-6 text-sm text-muted-foreground">
              {t('myFiles.preview.loading')}
            </p>
          }
        >
          <ViewerSlot entry={entry} item={item} />
        </Suspense>
      </PreviewBoundary>
    </PreviewControls>
  );
}

/** Xin presigned GET rồi giao cho trình xem (component nạp lười của `entry`). */
function ViewerSlot({
  entry,
  item,
}: {
  readonly entry: PreviewableViewer;
  readonly item: PreviewItem;
}): ReactNode {
  const source = usePreviewSource();
  const { data } = useSuspenseQuery({
    // Tệp cá nhân giữ đúng khoá cũ — `myFilesKeys.all` vẫn làm mới được nó.
    queryKey: source === MY_FILES_SOURCE ? myFilesKeys.url(item.id) : [source.key, 'url', item.id],
    queryFn: () => source.downloadUrl(item.id),
    // URL sống 60 phút: giữ 45 phút như hình nhỏ ở lưới.
    staleTime: 45 * 60_000,
    retry: false,
  });
  const Viewer = entry.component;
  return (
    <div className="h-full" data-viewer={entry.kind}>
      <Viewer url={data.url} name={item.name} sizeBytes={item.sizeBytes} />
    </div>
  );
}

/** Câu lý do cho màn hình tải về — theo mã, không theo văn bản lỗi. */
export function reasonOf(error: unknown, t: TFunction): string {
  if (error instanceof PreviewError) {
    if (error.code === 'tooLarge') {
      return t('myFiles.preview.error.tooLarge', { limit: humanSize(error.params['limit'] ?? 0) });
    }
    if (error.code === 'http') {
      return t('myFiles.preview.error.http', { status: error.params['status'] ?? '' });
    }
    return t(`myFiles.preview.error.${error.code}`);
  }
  if (error instanceof ApiRequestError) return errorMessage(error, t);
  return t('myFiles.preview.error.library');
}

/** Biểu tượng lớn + tiêu đề + lý do + tải về — lối ra chung của mọi ca không xem được. */
function PreviewFallback<T extends PreviewItem>({
  item,
  title,
  reason,
  onDownload,
}: {
  readonly item: T;
  readonly title: string;
  readonly reason: string;
  readonly onDownload: (item: T) => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <FileTypeIcon name={item.name} mimeType={item.mimeType} kind={item.kind} size="lg" />
      <p className="font-medium">{title}</p>
      <p className="max-w-md text-sm text-muted-foreground">{reason}</p>
      <Button onClick={() => onDownload(item)}>
        <Download aria-hidden />
        {t('myFiles.download')}
      </Button>
    </div>
  );
}

/**
 * Ranh giới lỗi của một lượt xem. React chỉ bắt lỗi render bằng class
 * component. Khung đặt `key={item.id}` cho thân nên đổi file là ranh giới mới.
 */
class PreviewBoundary extends Component<
  { readonly fallback: (error: unknown) => ReactNode; readonly children: ReactNode },
  { readonly failed: boolean; readonly error: unknown }
> {
  override state = { failed: false, error: null as unknown };

  static getDerivedStateFromError(error: unknown): { failed: boolean; error: unknown } {
    return { failed: true, error };
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback(this.state.error) : this.props.children;
  }
}
